/**
 * Shared, nationality-agnostic content for the "Spain visa from USA"
 * guide pages. Same content applies to every nationality applying through
 * BLS International in the US — the per-nationality overlay (fee band,
 * origin note, FAQs) lives in `spainUsaNationalityConfig.js`.
 *
 * All copy here is written in our own words based on the current BLS +
 * Spanish consulate USA requirements. Do NOT paste verbatim BLS text.
 */

export const BLS_CENTERS = [
  { city: "Boston, MA",        jurisdiction: "Consulate of Spain — Boston",         url: "https://usa.blsspainvisa.com/boston/" },
  { city: "Chicago, IL",       jurisdiction: "Consulate of Spain — Chicago",        url: "https://usa.blsspainvisa.com/chicago/" },
  { city: "Houston, TX",       jurisdiction: "Consulate of Spain — Houston",        url: "https://usa.blsspainvisa.com/houston/" },
  { city: "Los Angeles, CA",   jurisdiction: "Consulate of Spain — Los Angeles",    url: "https://usa.blsspainvisa.com/losangeles/" },
  { city: "Miami, FL",         jurisdiction: "Consulate of Spain — Miami",          url: "https://usa.blsspainvisa.com/miami/" },
  { city: "New York, NY",      jurisdiction: "Consulate of Spain — New York",       url: "https://usa.blsspainvisa.com/nyc/" },
  { city: "San Francisco, CA", jurisdiction: "Consulate of Spain — San Francisco",  url: "https://usa.blsspainvisa.com/sanfrancisco/" },
  { city: "Washington, DC",    jurisdiction: "Consulate of Spain — Washington DC",  url: "https://usa.blsspainvisa.com/washington/" },
];


/**
 * Classify a document-checklist line so we can pick the right Lucide icon.
 * Semantic, not decorative — an unmet requirement, a hard "no", or a neutral
 * heading all look different at a glance.
 *
 * Rules (checked in this order):
 *  - line ends with ":" → 'info' (it's an introductory / section header)
 *  - matches a negative-signal phrase → 'negative' (X, muted red)
 *  - otherwise → 'required' (Check, brand blue)
 */
export function classifyDocItem(text) {
  const s = (text || "").trim();
  if (!s) return "required";
  if (s.endsWith(":")) return "info";
  const t = s.toLowerCase();
  // Anything expressing "not accepted / not allowed / refused / don't do X".
  const negativeRe = /\b(not accepted|not allowed|are not accepted|is not accepted|will be refused|refused|does not permit|cannot be|do not buy|do not submit|do not include|do not use|are refused)\b/;
  if (negativeRe.test(t)) return "negative";
  return "required";
}

export const OVERVIEW_PARAGRAPHS = [
  "The Spain Schengen tourist visa (Type C) lets you enter Spain — and travel freely across the 29-country Schengen area — for tourism, family visits or short leisure trips of up to 90 days within any 180-day period.",
  "If Spain is your main destination (the country where you'll spend the most nights, or your first entry if nights are equal), you file with the Spanish consulate through BLS International. Files are submitted in person at one of eight BLS visa application centers across the United States, and each center is tied to a specific consular jurisdiction — you must apply at the center that covers your state of residence.",
  "You can submit no earlier than 180 days before your planned travel date, and you should submit at least 15 days before departure — the Spanish consulate needs at least 15 working days to decide, and may ask for more documents. Granting the visa is fully at the consulate's discretion, even if every document is in order.",
];

export const VISA_FEES_META = {
  service_charge_usd: 20,
  service_charge_note: "USD 20 BLS service charge is paid at the appointment (cash or debit card only). The service fee is waived for children under 6 years of age.",
  currency_note: "Consular visa fees are paid in USD, cash only, at the appointment. All fees are non-refundable — you pay whether or not the visa is granted.",
  effective_date_note: "Fees effective January 2026 (subject to change; non-refundable).",
};

export const DOCUMENTS_SECTIONS = [
  {
    title: "Application form",
    items: [
      "The Schengen visa application form, filled out completely and typed (no handwriting) and signed by the applicant in blue ink. Handwritten forms are refused.",
      "For minor applicants (under 18), both parents / legal guardians sign the form.",
    ],
  },
  {
    title: "Proof of jurisdiction (US address)",
    items: [
      "One recent document (issued within the last 3 months) confirming your US address inside the consular jurisdiction of the BLS center you're applying at.",
      "Accepted: utility bill (electricity, gas, water, internet), current lease agreement, mortgage statement, health insurance bill, or — for students — a valid student ID plus current-semester university transcript.",
      "Not accepted as jurisdiction proof: US driver's license, state ID card, or Aadhaar/foreign national ID.",
    ],
  },
  {
    title: "Passport",
    items: [
      "Original ordinary passport, issued within the last 10 years.",
      "At least two blank facing pages available for the visa sticker.",
      "Validity of at least 3 months beyond your planned departure date from the Schengen area.",
      "One photocopy of the biodata page.",
    ],
  },
  {
    title: "Proof of legal US residence",
    items: [
      "A document showing your legal US residence that permits re-entry to the US after your Schengen trip. Depending on status:",
      "Permanent residents: original US Green Card + one photocopy of the front and back.",
      "Long-term work / study visa holders: valid US visa stamp in your passport + latest I-797 approval notice (H1B, L1, O1, etc.) or I-20 with recent travel endorsement (F1). OPT-EAD holders should also carry an approved advance parole document for safer re-entry.",
      "Refugee / asylee: refugee travel document or reentry permit.",
      "If your document does not permit re-entry to the US, the file will be refused and you must apply from your country of nationality.",
    ],
  },
  {
    title: "Cover letter",
    items: [
      "A cover letter (in English or Spanish) addressed to the Spanish consulate. State the purpose of your trip, dates, main destination, itinerary summary, who is paying, your ties to the US, and your intention to return to the US within 90 days.",
    ],
  },
  {
    title: "Proof of travel",
    items: [
      "Round-trip flight reservation showing your name, dates and itinerary — a reservation, not a paid ticket. Do not buy your tickets until the consulate specifically requests it.",
    ],
  },
  {
    title: "Proof of accommodation (proof of stay)",
    items: [
      "Confirmed hotel bookings for every night of your trip, in the applicant's name and matching your travel dates exactly. Screenshots or generic OTA quotes are not accepted.",
      "Staying with family or friends: original Carta de Invitación issued by the Spanish authorities in Spain (not a private letter).",
      "Organised tour / cruise: a formal letter from the operator confirming your itinerary and inclusions.",
    ],
  },
  {
    title: "Proof of funds",
    items: [
      "Three months of your most recent personal bank statements. Statements must be in USD or Euros; joint accounts must clearly show the applicant as an account holder.",
      "Minimum available balance: USD 110 per day of the intended stay, with an ending balance of at least USD 1,100 (or the equivalent in Euros).",
      "If a sponsor is paying, add the sponsor's 3-month statements plus a signed sponsorship letter and proof of the sponsor's own status/relationship.",
    ],
  },
  {
    title: "Travel medical insurance",
    items: [
      "EU-compliant travel medical insurance covering the entire Schengen stay.",
      "Minimum medical coverage of €30,000 (or USD equivalent) with zero deductible, valid across all Schengen countries, and explicitly including emergency medical care and repatriation of remains.",
      "The certificate must state the applicant's full name and passport number.",
    ],
  },
  {
    title: "Proof of employment / status",
    items: [
      "Employed: letter from employer on letterhead (with logo and stamp) confirming your position, salary, length of employment, approved leave dates, and that you are expected to return to work.",
      "Self-employed: state / IRS business registration or DBA + last tax return + 3 months of business bank statements.",
      "Retirees: SSA benefit letter or equivalent pension statement.",
      "Students: valid I-20 and a current enrollment / good-standing letter from the university.",
      "Business travelers: invitation letter from the host company in Spain plus an assignment / travel letter from the US employer.",
    ],
  },
  {
    title: "Additional documents for minors",
    items: [
      "Original birth certificate + certified Spanish translation.",
      "If parents are divorced / separated: legal custody documentation.",
      "If the minor is not travelling with both parents: a notarized and apostilled parental authorization letter from the non-travelling parent(s), plus copies of the non-travelling parent(s)' passport and US residence document.",
      "Both parents sign the application form in front of the BLS agent.",
    ],
  },
  {
    title: "General notes",
    items: [
      "All documents must be in English or Spanish. Foreign-language documents require a certified translation.",
      "Financial evidence must be in USD or Euros. Foreign-currency statements are not accepted.",
      "Do not submit screenshots — the consulate requires original PDFs or printouts from the issuing institution.",
    ],
  },
];

export const PHOTO_SPEC = {
  size_inches: '2×2 inches (51 × 51 mm)',
  background: "Plain white background — no textures, shadows or patterns.",
  age: "Taken within the last 6 months and a true recent likeness of the applicant.",
  face: "Face centered, taking up roughly 70–80% of the frame, from mid-chest to just above the head. Both ears must be visible.",
  expression: "Neutral expression, mouth closed, no smiling. Eyes open and looking straight at the camera.",
  accessories: "No glasses (including clear lenses), no hats or head coverings — except headwear worn for religious reasons that does not obscure any part of the face.",
  print: "Printed on glossy photographic paper, unretouched. Digital photos or home-printed inkjet copies are typically refused.",
};

export const PROCESSING_TIME_POINTS = [
  "BLS forwards your complete file to the Spanish consulate on the next working day after receipt.",
  "The consulate requires a minimum of 15 working days to process a Schengen tourist visa.",
  "The consulate may request additional documents or an interview — respond promptly to avoid rejection.",
  "Submit your application at least 15 days before your intended travel date; earlier submissions (up to 180 days ahead) are strongly recommended during peak season (May–September).",
  "The visa is affixed to your passport as a sticker; BLS notifies you when your passport is ready for pickup or courier return.",
];
